import { Box, Typography, TextField, Grid, Chip, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { useEffect, useMemo, useRef, useState } from 'react';
import useDebounce from '../../../../../../../lib/useDebounce';
import { ICarrinhoItem } from '..';
import SelectSetoresMultiple from '../../../../../../../components/SelectsSimpleApiQuery/SelectSetores/Multiple';
import { IDataSetores } from '../../../../../../../types/api/estoque/pesquisar-setores';
import SelectDepartamentoEstoqueMultiple
    from '../../../../../../../components/SelectsSimpleApiQuery/SelectDepartamentoEstoque/Multiple';
import { IDataDepartamentosEstoque } from '../../../../../../../types/api/estoque/pesquisar-departamentos-estoque';
import Categorias from './Categorias';
import SelectIngredienteAtivoMultiple
    from "../../../../../../../components/SelectsSimpleApiQuery/SelectIngredienteAtivo/Multiple";
import { IDataIngredientesAtivos } from "../../../../../../../types/api/estoque/pesquisar-ingredientes-ativos";
import ClearIcon from "@mui/icons-material/Clear";
import { IDataPublicos } from "../../../../../../../types/api/geral/pesquisar-publicos";
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';
import Produtos from './Produtos';

export default function ListaProdutos({
    defaultValueCliente,
    onAddItem
}: ListaProdutosProps) {

    const searchInputRef = useRef<HTMLInputElement>(null);
    const [keyword, setKeyword] = useState("")
    const [filters, setFilters] = useState<TFilters>(defaultFilters)
    const [exibicao, setExibicao] = useState<"grid" | "list">("grid")
    const dbKeyword = useDebounce(keyword)

    const handleFilterChange = (filter: keyof TFilters, value: any) => {
        let newFilters = {};

        if (filters == null) {
            newFilters = { [filter]: value }
        } else {
            newFilters = { ...filters, [filter]: value }
        }
        setFilters(newFilters as TFilters)

    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(e.target.value)
    }

    const hasActiveFilters = useMemo(() => {
        return Boolean(
            dbKeyword ||
            filters.departamentos?.length ||
            filters.setores?.length ||
            filters.ingredientes?.length ||
            filters.categoria?.length
        );
    }, [dbKeyword, filters])

    useEffect(() => {
        restoreFocusToSearch();
    }, [filters]);


    const restoreFocusToSearch = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };


    return (
        <Box p={2} mb={4}>
            <Typography variant='h6' mb={2}>Produtos</Typography>
            <Grid container spacing={2} mb={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        value={keyword}
                        onChange={handleSearch}
                        fullWidth
                        autoFocus
                        inputRef={searchInputRef}
                        placeholder="Pesquisar"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                ...(keyword && {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <ClearIcon
                                                onClick={() => setKeyword('')}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#888'
                                                }}
                                            />
                                        </InputAdornment>
                                    )
                                })
                            }
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                    <SelectDepartamentoEstoqueMultiple
                        value={filters.departamentos}
                        onChange={(dpto) => handleFilterChange('departamentos', dpto)}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                    <SelectSetoresMultiple
                        value={filters.setores}
                        onChange={(setores) => handleFilterChange('setores', setores)}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                    <SelectIngredienteAtivoMultiple
                        value={filters.ingredientes}
                        onChange={(ingredientes) => handleFilterChange('ingredientes', ingredientes)}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={filters.exibir_apenas_com_estoque}
                                onChange={(e) => handleFilterChange('exibir_apenas_com_estoque', e.target.checked)}
                                name="exibir_apenas_com_estoque"
                            />
                        }
                        label="Exibir somente produtos com estoque"
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center">
                        <ToggleButtonGroup
                            size="small"
                            aria-label="Small sizes"
                            value={exibicao}
                            onChange={(_, value) => setExibicao(value)}
                            exclusive={true}
                        >
                            <ToggleButton value="grid" key="grid">
                                <GridViewOutlinedIcon />
                            </ToggleButton>

                            <ToggleButton value="list" key="list">
                                <ListOutlinedIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Grid>
            </Grid>

            {filters?.categoria && (
                <Box mb={2}>
                    <Chip
                        label={filters.categoria.hierarquia}
                        onDelete={() => {
                            setFilters(defaultFilters);
                        }}
                        color="primary"
                    />
                </Box>
            )}

            {
                !hasActiveFilters &&
                <Categorias onCategoriaClick={(id, hierarquia) => handleFilterChange('categoria', { id, hierarquia })} />
            }

            {hasActiveFilters && (
                <Produtos
                    defaultValueCliente={defaultValueCliente}
                    filters={{
                        pesquisar: typeof dbKeyword === 'string' ? dbKeyword : "",
                        departamentos: filters.departamentos || [],
                        setores: filters.setores || [],
                        ingredientes: filters.ingredientes || [],
                        exibir_apenas_com_estoque: filters.exibir_apenas_com_estoque,
                        categoria: filters.categoria || null
                    }}
                    keyword={typeof dbKeyword === 'string' ? dbKeyword : ''}
                    onAddItem={onAddItem}
                    viewMode={exibicao}
                />
            )}
        </Box>
    );
}

const defaultFilters: TFilters = {
    pesquisar: "",
    departamentos: [],
    setores: [],
    ingredientes: [],
    exibir_apenas_com_estoque: true,
    categoria: null
}

interface ListaProdutosProps {
    onAddItem: (item: ICarrinhoItem) => void;
    defaultValueCliente?: IDataPublicos;
}

type TFilters = {
    pesquisar: string | null
    departamentos: IDataDepartamentosEstoque[] | null
    setores: IDataSetores[] | null
    ingredientes: IDataIngredientesAtivos[] | null
    exibir_apenas_com_estoque: boolean
    categoria: any
}

